import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { 
  Users,
  UserPlus,
  Mail,
  Phone,
  Link as LinkIcon,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Loader2,
  Search,
  Copy,
  ExternalLink,
  UserCheck,
  Ban
} from 'lucide-react';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';

export default function ParentManagementPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [parents, setParents] = useState([]);
  const [children, setChildren] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Dialogs
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  
  // Selected items
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedChildren, setSelectedChildren] = useState([]);
  
  // Invite form
  const [inviteForm, setInviteForm] = useState({
    email: '',
    first_name: '',
    last_name: '',
    phone: '',
    relationship_to_child: 'parent'
  });
  const [inviteLink, setInviteLink] = useState('');
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [parentsRes, childrenRes] = await Promise.all([
        api.get('/parent-portal/admin/parents'),
        api.get('/patients')
      ]);
      setParents(parentsRes.data.parents || []);
      setChildren(childrenRes.data || []);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleInvite = async () => {
    if (!inviteForm.email || !inviteForm.first_name || !inviteForm.last_name) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (selectedChildren.length === 0) {
      toast.error('Please select at least one child to link');
      return;
    }

    setSaving(true);
    try {
      const response = await api.post('/parent-portal/invite', {
        ...inviteForm,
        child_ids: selectedChildren
      });

      if (response.data.success) {
        setInviteLink(response.data.invite_link);
        toast.success('Invitation created!');
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (parentId) => {
    try {
      await api.put(`/parent-portal/admin/approve-parent/${parentId}`);
      toast.success('Parent approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve parent');
    }
  };

  const handleLinkChildren = async () => {
    if (!selectedParent || selectedChildren.length === 0) return;

    setSaving(true);
    try {
      await api.put(`/parent-portal/admin/link-children/${selectedParent.id}`, {
        child_ids: selectedChildren
      });
      toast.success('Children linked successfully');
      setShowLinkDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to link children');
    } finally {
      setSaving(false);
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success('Link copied to clipboard');
  };

  const openLinkDialog = (parent) => {
    setSelectedParent(parent);
    setSelectedChildren(parent.linked_children || []);
    setShowLinkDialog(true);
  };

  const openPreviewDialog = (parent) => {
    setSelectedParent(parent);
    setShowPreviewDialog(true);
  };

  const filteredParents = parents.filter(p => 
    `${p.first_name} ${p.last_name} ${p.email}`.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (parent) => {
    if (!parent.is_approved) {
      return <Badge className="bg-amber-100 text-amber-800">Pending Approval</Badge>;
    }
    if (!parent.is_active) {
      return <Badge className="bg-blue-100 text-blue-800">Invite Pending</Badge>;
    }
    return <Badge className="bg-green-100 text-green-800">Active</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#1e3a5f]" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-management">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-[#1e3a5f]">Parent Management</h1>
          <p className="text-slate-600">Manage parent accounts and link them to children</p>
        </div>
        <Button 
          className="bg-[#2a9d8f] hover:bg-[#238b7e]"
          onClick={() => {
            setInviteForm({ email: '', first_name: '', last_name: '', phone: '', relationship_to_child: 'parent' });
            setSelectedChildren([]);
            setInviteLink('');
            setShowInviteDialog(true);
          }}
        >
          <UserPlus className="w-4 h-4 mr-2" />
          Invite Parent
        </Button>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600">Total Parents</p>
                <p className="text-2xl font-bold text-blue-800">{parents.length}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600">Active</p>
                <p className="text-2xl font-bold text-green-800">
                  {parents.filter(p => p.is_active && p.is_approved).length}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-amber-600">Pending Approval</p>
                <p className="text-2xl font-bold text-amber-800">
                  {parents.filter(p => !p.is_approved).length}
                </p>
              </div>
              <Clock className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-600">Invite Pending</p>
                <p className="text-2xl font-bold text-purple-800">
                  {parents.filter(p => p.is_approved && !p.is_active).length}
                </p>
              </div>
              <Send className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Search parents..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Parents List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#2a9d8f]" />
            Parent Accounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredParents.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>No parents found</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredParents.map(parent => (
                <div key={parent.id} className="p-4 border rounded-lg hover:bg-slate-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-[#1e3a5f]">
                          {parent.first_name} {parent.last_name}
                        </h4>
                        {getStatusBadge(parent)}
                        <Badge variant="outline" className="text-xs">
                          {parent.relationship_to_child || 'parent'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {parent.email}
                        </span>
                        {parent.phone && (
                          <span className="flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {parent.phone}
                          </span>
                        )}
                      </div>
                      {/* Linked Children */}
                      <div className="mt-2">
                        <span className="text-sm text-slate-500">Linked Children: </span>
                        {parent.children?.length > 0 ? (
                          <span className="text-sm font-medium">
                            {parent.children.map(c => `${c.first_name} ${c.last_name}`).join(', ')}
                          </span>
                        ) : (
                          <span className="text-sm text-amber-600">None linked</span>
                        )}
                      </div>
                      <p className="text-xs text-slate-400 mt-1">
                        Registered: {format(parseISO(parent.created_at), 'MMM d, yyyy')}
                        {parent.registration_type === 'invitation' && ' (Invited)'}
                        {parent.registration_type === 'self_registration' && ' (Self-registered)'}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      {!parent.is_approved && (
                        <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleApprove(parent.id)}>
                          <UserCheck className="w-4 h-4 mr-1" />
                          Approve
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => openLinkDialog(parent)}>
                        <LinkIcon className="w-4 h-4 mr-1" />
                        Link Children
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openPreviewDialog(parent)} title="Preview as Parent">
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <UserPlus className="w-5 h-5" />
              Invite Parent
            </DialogTitle>
          </DialogHeader>

          {!inviteLink ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>First Name *</Label>
                  <Input
                    value={inviteForm.first_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, first_name: e.target.value })}
                    placeholder="First name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Last Name *</Label>
                  <Input
                    value={inviteForm.last_name}
                    onChange={(e) => setInviteForm({ ...inviteForm, last_name: e.target.value })}
                    placeholder="Last name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                  placeholder="parent@email.com"
                />
              </div>

              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  type="tel"
                  value={inviteForm.phone}
                  onChange={(e) => setInviteForm({ ...inviteForm, phone: e.target.value })}
                  placeholder="+44 7..."
                />
              </div>

              <div className="space-y-2">
                <Label>Relationship</Label>
                <Select 
                  value={inviteForm.relationship_to_child}
                  onValueChange={(v) => setInviteForm({ ...inviteForm, relationship_to_child: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="parent">Parent</SelectItem>
                    <SelectItem value="guardian">Legal Guardian</SelectItem>
                    <SelectItem value="carer">Carer</SelectItem>
                    <SelectItem value="foster_parent">Foster Parent</SelectItem>
                    <SelectItem value="grandparent">Grandparent</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Link to Children *</Label>
                <div className="max-h-40 overflow-y-auto border rounded-lg p-2 space-y-2">
                  {children.map(child => (
                    <div key={child.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`child-${child.id}`}
                        checked={selectedChildren.includes(child.id)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedChildren([...selectedChildren, child.id]);
                          } else {
                            setSelectedChildren(selectedChildren.filter(id => id !== child.id));
                          }
                        }}
                      />
                      <Label htmlFor={`child-${child.id}`} className="cursor-pointer">
                        {child.first_name} {child.last_name}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setShowInviteDialog(false)}>Cancel</Button>
                <Button onClick={handleInvite} disabled={saving} className="bg-[#2a9d8f]">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Create Invitation
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 rounded-lg text-center">
                <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                <p className="font-medium text-green-800">Invitation Created!</p>
              </div>

              <div className="space-y-2">
                <Label>Invite Link</Label>
                <div className="flex gap-2">
                  <Input value={inviteLink} readOnly className="font-mono text-sm" />
                  <Button variant="outline" onClick={copyInviteLink}>
                    <Copy className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <p className="text-sm text-slate-600">
                Send this link to the parent. They can use it to set their password and activate their account.
                The link expires in 7 days.
              </p>

              <DialogFooter>
                <Button onClick={() => { setShowInviteDialog(false); setInviteLink(''); }} className="bg-[#1e3a5f]">
                  Done
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Link Children Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <LinkIcon className="w-5 h-5" />
              Link Children to {selectedParent?.first_name} {selectedParent?.last_name}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Select which children this parent should have access to:
            </p>
            
            <div className="max-h-60 overflow-y-auto border rounded-lg p-2 space-y-2">
              {children.map(child => (
                <div key={child.id} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded">
                  <Checkbox
                    id={`link-child-${child.id}`}
                    checked={selectedChildren.includes(child.id)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedChildren([...selectedChildren, child.id]);
                      } else {
                        setSelectedChildren(selectedChildren.filter(id => id !== child.id));
                      }
                    }}
                  />
                  <Label htmlFor={`link-child-${child.id}`} className="cursor-pointer flex-1">
                    <span className="font-medium">{child.first_name} {child.last_name}</span>
                    {child.nhs_number && <span className="text-xs text-slate-500 ml-2">NHS: {child.nhs_number}</span>}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Cancel</Button>
            <Button onClick={handleLinkChildren} disabled={saving} className="bg-[#2a9d8f]">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <LinkIcon className="w-4 h-4 mr-2" />}
              Save Links
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreviewDialog} onOpenChange={setShowPreviewDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#1e3a5f]">
              <Eye className="w-5 h-5" />
              Parent View Preview
            </DialogTitle>
          </DialogHeader>

          {selectedParent && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium mb-2">
                  {selectedParent.first_name} {selectedParent.last_name} can see:
                </h4>
                <ul className="text-sm text-slate-600 space-y-1">
                  <li>• Their linked children's profiles (limited info)</li>
                  <li>• Upcoming appointments for their children</li>
                  <li>• Documents shared by therapists</li>
                  <li>• Events marked as visible to parents</li>
                  <li>• Invoices and payment options</li>
                  <li>• Receipt downloads</li>
                </ul>
              </div>

              <div className="p-4 bg-amber-50 rounded-lg">
                <h4 className="font-medium text-amber-800 mb-2">Cannot see:</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Other children's information</li>
                  <li>• Clinical notes (unless explicitly shared)</li>
                  <li>• Staff information</li>
                  <li>• Internal communications</li>
                  <li>• Other parents' data</li>
                </ul>
              </div>

              <div className="p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2">Linked Children:</h4>
                {selectedParent.children?.length > 0 ? (
                  <ul className="text-sm text-blue-700">
                    {selectedParent.children.map(c => (
                      <li key={c.id}>• {c.first_name} {c.last_name}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-blue-600">No children linked yet</p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button onClick={() => setShowPreviewDialog(false)} className="bg-[#1e3a5f]">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
